/**
 * ElecBid Pipeline — the all-in-one command center + client-communications scheduler.
 *
 * Dylan 2026-07-20. A hedge against losing Jobber API access: one workspace that
 * surfaces the whole client → request → quote → job → invoice pipeline from the
 * data ElecBid already caches, plus the field-service capability Jobber has that
 * ElecBid didn't — SCHEDULING client texts + emails.
 *
 * Three sub-tabs:
 *   Overview       — pipeline snapshot (requests, quotes, jobs, invoices, visits).
 *   Clients        — CRM search over the client cache; message any client.
 *   Communications — templates, the scheduled queue, automation rules, and the
 *                    ARMED master switch. SAFE MODE by default: messages are
 *                    drafted, previewed and test-sent to yourself, but nothing
 *                    reaches a real client until the owner arms it.
 *
 * All data goes through /api/pipeline (service key, admin-gated) — this component
 * never queries Supabase directly. Inline styles only (project rule).
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";
import Client360 from "./PipelineClient360";

const API = "/api/pipeline";

// ── Palette (matches the dark app shell / FlowStrip) ─────────────────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", card2: "#12151b",
  border: "#2a3140", lime: "#a5ce3a", limeDim: "rgba(165,206,58,.12)",
  text: "#e8eaf0", muted: "#8892a4", faint: "#5a6472",
  blue: "#3b82f6", amber: "#f59e0b", green: "#10b981", red: "#ef4444", purple: "#8b5cf6",
};

const money = (n) => (n == null || isNaN(+n) ? "—" : "$" + Math.round(+n).toLocaleString("en-CA"));
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—";
const titleCase = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Never throws — a network failure returns { ok:false, error } so callers always
// reach their setBusy(false)/error branch instead of hanging in a loading state.
async function callApi(action, extra = {}) {
  try {
    const r = await apiFetch(`${API}?action=${encodeURIComponent(action)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, ...extra }),
    });
    const j = await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }));
    if (!r.ok && j.ok === undefined) return { ok: false, error: `HTTP ${r.status}` };
    return j;
  } catch (e) {
    return { ok: false, error: e?.message || "Network error — check your connection." };
  }
}

// ── Shared UI atoms ──────────────────────────────────────────────────────────
const S = {
  wrap: { padding: "16px 16px 90px", maxWidth: 1160, margin: "0 auto", fontFamily: "'Roboto',sans-serif", color: C.text },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 14 },
  h2: { fontFamily: "'Bebas Neue',sans-serif", fontSize: 22, letterSpacing: ".04em", color: C.text, margin: 0 },
  sectionH: { fontSize: 12, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 },
  label: { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4, display: "block" },
  input: { width: "100%", padding: "9px 11px", background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" },
  btn: { padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  btnPri: { padding: "8px 15px", borderRadius: 8, border: "none", background: C.lime, color: "#12200a", fontSize: 13, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" },
  btnGhost: { padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.border}`, background: "transparent", color: C.muted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  th: { textAlign: "left", padding: "8px 10px", fontSize: 10.5, fontWeight: 800, color: C.muted, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" },
  td: { padding: "9px 10px", borderBottom: `1px solid ${C.card2}`, fontSize: 13.5, verticalAlign: "middle" },
};

function Btn({ kind = "btn", children, ...p }) {
  const style = kind === "pri" ? S.btnPri : kind === "ghost" ? S.btnGhost : S.btn;
  return <button {...p} style={{ ...style, ...(p.disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}), ...(p.style || {}) }}>{children}</button>;
}

function Chip({ tone = "muted", children }) {
  const tones = {
    muted: [C.card, C.muted, C.border], draft: ["rgba(136,146,164,.14)", C.muted, C.border],
    scheduled: ["rgba(59,130,246,.14)", C.blue, "rgba(59,130,246,.4)"],
    sent: ["rgba(16,185,129,.14)", C.green, "rgba(16,185,129,.4)"],
    failed: ["rgba(239,68,68,.14)", C.red, "rgba(239,68,68,.4)"],
    cancelled: ["rgba(136,146,164,.10)", C.faint, C.border],
    active: ["rgba(245,158,11,.14)", C.amber, "rgba(245,158,11,.4)"],
    email: ["rgba(59,130,246,.12)", C.blue, "rgba(59,130,246,.35)"],
    sms: ["rgba(16,185,129,.12)", C.green, "rgba(16,185,129,.35)"],
    lime: [C.limeDim, C.lime, "rgba(165,206,58,.4)"],
  };
  const [bg, fg, bd] = tones[tone] || tones.muted;
  return <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: fg, border: `1px solid ${bd}`, whiteSpace: "nowrap" }}>{children}</span>;
}

function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.72)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: width, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.surface, zIndex: 1 }}>
          <div style={S.h2}>{title}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.muted, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>
        <div style={{ padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={S.label}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: C.faint, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Spinner({ label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: 40, color: C.muted, fontSize: 13 }}>
      <div style={{ width: 20, height: 20, border: `2px solid ${C.limeDim}`, borderTopColor: C.lime, borderRadius: "50%", animation: "pspin .8s linear infinite" }} />
      <style>{`@keyframes pspin{to{transform:rotate(360deg)}}`}</style>
      {label || "Loading…"}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ color: C.faint, fontSize: 13, padding: "22px 10px", textAlign: "center" }}>{children}</div>;
}

const CHANNEL_ICON = { email: "✉️", sms: "💬" };

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════
function OverviewTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    setErr(null);
    const r = await callApi("overview");
    if (r.ok) setData(r); else setErr(r.error || "Failed to load");
  }, []);
  useEffect(() => { load(); }, [load]);

  if (err) return <div style={{ ...S.card, borderColor: "rgba(239,68,68,.4)", color: C.red }}>Couldn't load pipeline: {err}</div>;
  if (!data) return <Spinner label="Loading pipeline…" />;
  const st = data.stats;

  const tiles = [
    ["Active requests", st.activeRequests, C.amber],
    ["Open quotes", st.openQuotes, C.blue],
    ["Open jobs", st.openJobs, C.lime],
    ["Owing invoices", st.owingInvoices, C.red],
    ["$ outstanding", money(st.totalOwing), C.red],
    ["Upcoming visits", st.upcomingVisits, C.purple],
    ["Comms scheduled", st.commsScheduled, C.green],
    ["Comms drafts", st.commsDrafts, C.muted],
  ];

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
        {tiles.map(([label, val, color]) => (
          <div key={label} style={{ ...S.card, margin: 0, padding: "14px 16px" }}>
            <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.1 }}>{val}</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 }}>
        <div style={S.card}>
          <div style={S.sectionH}>Upcoming visits</div>
          {!data.upcomingVisits.length ? <Empty>No scheduled visits ahead.</Empty> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.upcomingVisits.map((v) => (
                <div key={v.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 10px", background: C.card2, borderRadius: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.title || (v.jobNumber ? `Job #${v.jobNumber}` : "Visit")}</div>
                    {v.jobNumber && <div style={{ fontSize: 11, color: C.faint }}>Job #{v.jobNumber}</div>}
                  </div>
                  <div style={{ fontSize: 12, color: C.lime, whiteSpace: "nowrap" }}>{fmtDateTime(v.startAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={S.sectionH}>Recent requests</div>
          {!data.recentRequests.length ? <Empty>No requests cached.</Empty> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.recentRequests.map((r) => (
                <div key={r.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 10px", background: C.card2, borderRadius: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.clientName || "—"}</div>
                    <div style={{ fontSize: 11.5, color: C.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.title || titleCase(r.status)}</div>
                  </div>
                  <Chip tone={r.bucket === "active" ? "active" : "muted"}>{titleCase(r.bucket)}</Chip>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={S.sectionH}>Open quotes (awaiting response)</div>
          {!data.openQuotes.length ? <Empty>No open quotes.</Empty> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.openQuotes.map((q) => (
                <div key={q.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 10px", background: C.card2, borderRadius: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{q.clientName || q.title}</div>
                    <div style={{ fontSize: 11.5, color: C.faint }}>{titleCase(q.state)}</div>
                  </div>
                  <div style={{ fontSize: 13, color: C.lime, fontWeight: 700, whiteSpace: "nowrap" }}>{money(q.total)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={S.sectionH}>Outstanding invoices</div>
          {!data.owingInvoices.length ? <Empty>Nothing owing.</Empty> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.owingInvoices.map((iv) => (
                <div key={iv.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "8px 10px", background: C.card2, borderRadius: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{iv.clientName || (iv.number ? `Invoice #${iv.number}` : "Invoice")}</div>
                    <div style={{ fontSize: 11.5, color: C.faint }}>{titleCase(iv.status)}</div>
                  </div>
                  <div style={{ fontSize: 13, color: C.red, fontWeight: 700, whiteSpace: "nowrap" }}>{money(iv.balance)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ textAlign: "right", marginTop: 12 }}>
        <Btn kind="ghost" onClick={load}>↻ Refresh</Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CLIENTS (CRM)
// ═══════════════════════════════════════════════════════════════════════════
function ClientsTab({ onMessage }) {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("client");
  // ?q= seeds the search box so other pages (e.g. the Expenses client column)
  // can deep-link straight to a client by name.
  const initialQ = useRef(params.get("q") || "");
  const [q, setQ] = useState(initialQ.current);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0); // guards against out-of-order responses

  const search = useCallback(async (term) => {
    const mine = ++seq.current;
    setLoading(true);
    const r = await callApi("clients", { q: term, limit: 80 });
    if (mine !== seq.current) return; // a newer search superseded this one
    setRows(r.ok ? r.clients : []);
    setLoading(false);
  }, []);

  useEffect(() => { search(initialQ.current); return () => clearTimeout(timer.current); }, [search]);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 280);
  };

  // Click a client → the full 360 (URL-driven, so drill-downs deep-link and the
  // browser back button walks back out of them).
  const openClient = (c) => {
    const n = new URLSearchParams(params);
    n.set("client", c.id); n.delete("job"); n.delete("request");
    setParams(n);
  };

  if (selectedId) return <Client360 clientId={selectedId} onMessage={onMessage} />;

  return (
    <div>
      <div style={{ ...S.card, display: "flex", gap: 10, alignItems: "center" }}>
        <input style={S.input} placeholder="Search clients by name, phone, email or address…" value={q} onChange={(e) => onChange(e.target.value)} />
      </div>
      <div style={S.card}>
        {loading ? <Spinner /> : !rows.length ? <Empty>No clients found.</Empty> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={S.th}>Client</th><th style={S.th}>Phone</th><th style={S.th}>Email</th><th style={S.th}>Address</th><th style={S.th}></th></tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} style={{ cursor: "pointer" }} onClick={() => openClient(c)}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{c.name}</td>
                    <td style={S.td}>{c.phone || <span style={{ color: C.faint }}>—</span>}</td>
                    <td style={S.td}>{c.email || <span style={{ color: C.faint }}>—</span>}</td>
                    <td style={{ ...S.td, color: C.muted, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.address || "—"}</td>
                    <td style={S.td}>
                      <Btn kind="ghost" onClick={(e) => { e.stopPropagation(); onMessage(c); }}>Message</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMMUNICATIONS
// ═══════════════════════════════════════════════════════════════════════════
function ArmBanner({ settings, onToggle, busy }) {
  const armed = !!settings?.armed;
  return (
    <div style={{
      ...S.card, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap",
      borderColor: armed ? "rgba(16,185,129,.5)" : "rgba(245,158,11,.5)",
      background: armed ? "rgba(16,185,129,.08)" : "rgba(245,158,11,.07)",
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: armed ? C.green : C.amber }}>
          {armed ? "🟢 LIVE — messages will send to clients" : "🟡 SAFE MODE — nothing sends to clients"}
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, maxWidth: 640 }}>
          {armed
            ? "Scheduled messages fire automatically at their due time. Test sends still go to you."
            : "Drafts, previews and test-to-yourself work now. Flip the switch when you're ready to actually text/email clients."}
        </div>
      </div>
      <Btn kind={armed ? "btn" : "pri"} disabled={busy} onClick={() => onToggle(!armed)}
        style={armed ? { borderColor: C.red, color: C.red } : {}}>
        {busy ? "…" : armed ? "Disarm (back to safe mode)" : "Arm — go live"}
      </Btn>
    </div>
  );
}

const PLACEHOLDER_HELP = "{{client_first}} {{client_name}} {{address}} {{date}} {{time}} {{quote_number}} {{invoice_number}} {{amount}} {{review_link}} {{owner_name}}";

function ComposeModal({ initialClient, templates, settings, onClose, onDone }) {
  const [channel, setChannel] = useState("sms");
  const [client, setClient] = useState(initialClient || null);
  const [clientQ, setClientQ] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [to, setTo] = useState(initialClient ? (initialClient.phone || "") : "");
  const [when, setWhen] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);
  const seq = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);

  const chTemplates = templates.filter((t) => t.channel === channel);

  const applyTemplate = useCallback(async (tid, ch, cl) => {
    if (!tid) { return; }
    const r = await callApi("template-preview", { template_id: tid, channel: ch, clientId: cl?.id });
    if (r.ok) { setSubject(r.subject || ""); setBody(r.body || ""); }
  }, []);

  const pickClient = (c) => {
    setClient(c); setClientResults([]); setClientQ("");
    setTo(channel === "sms" ? (c.phone || "") : (c.email || ""));
    if (templateId) applyTemplate(templateId, channel, c);
  };

  const onClientSearch = (v) => {
    setClientQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!v.trim()) { setClientResults([]); return; }
      const mine = ++seq.current;
      const r = await callApi("clients", { q: v, limit: 8 });
      if (mine !== seq.current) return; // superseded by a newer keystroke
      setClientResults(r.ok ? r.clients : []);
    }, 250);
  };

  const onChannel = (ch) => {
    setChannel(ch); setTemplateId("");
    if (client) setTo(ch === "sms" ? (client.phone || "") : (client.email || ""));
  };

  const onTemplate = (tid) => { setTemplateId(tid); applyTemplate(tid, channel, client); };

  const doSave = async (mode) => {
    setBusy(true); setMsg(null);
    const payload = {
      channel, clientId: client?.id, clientName: client?.name, to,
      template_id: templateId || null, subject, body,
    };
    let r;
    if (mode === "test") {
      r = await callApi("send-now", { ...payload, test: true });
      if (r.ok) setMsg({ tone: "sent", text: `Test ${channel} sent to you (${r.to}).` });
      else setMsg({ tone: "failed", text: `Test failed — ${r.error || "unknown"}` });
    } else if (mode === "send") {
      r = await callApi("send-now", { ...payload });
      if (r.ok) { setMsg({ tone: "sent", text: `Sent to ${r.to}.` }); setTimeout(onDone, 700); }
      else setMsg({ tone: "failed", text: r.error === "disarmed" ? "Safe mode is on — arm comms to send to clients (or use Send test)." : `Send failed: ${r.error}` });
    } else {
      const status = mode === "schedule" ? "scheduled" : "draft";
      r = await callApi("schedule", { ...payload, status, scheduled_for: when ? new Date(when).toISOString() : null });
      if (r.ok) { setMsg({ tone: "sent", text: status === "scheduled" ? "Queued." : "Saved as draft." }); setTimeout(onDone, 600); }
      else setMsg({ tone: "failed", text: `Save failed: ${r.error}` });
    }
    setBusy(false);
  };

  return (
    <Modal title="New message" onClose={onClose} width={620}>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {["sms", "email"].map((ch) => (
          <button key={ch} onClick={() => onChannel(ch)} style={{
            flex: 1, padding: "9px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13,
            border: `1px solid ${channel === ch ? C.lime : C.border}`, color: channel === ch ? C.lime : C.muted,
            background: channel === ch ? C.limeDim : C.card,
          }}>{CHANNEL_ICON[ch]} {ch === "sms" ? "Text (SMS)" : "Email"}</button>
        ))}
      </div>

      <Field label="Client">
        {client ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 11px", background: C.card2, borderRadius: 8 }}>
            <span style={{ fontSize: 14 }}>{client.name} {client.phone ? `· ${client.phone}` : ""}</span>
            <Btn kind="ghost" onClick={() => { setClient(null); setTo(""); }}>Change</Btn>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <input style={S.input} placeholder="Search client…" value={clientQ} onChange={(e) => onClientSearch(e.target.value)} />
            {clientResults.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: "auto" }}>
                {clientResults.map((c) => (
                  <div key={c.id} onClick={() => pickClient(c)} style={{ padding: "8px 11px", cursor: "pointer", borderBottom: `1px solid ${C.card2}`, fontSize: 13.5 }}>
                    {c.name} <span style={{ color: C.faint }}>{c.phone || c.email || ""}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Field>

      <Field label="Template">
        <select style={{ ...S.input, appearance: "auto" }} value={templateId} onChange={(e) => onTemplate(e.target.value)}>
          <option value="">— None (write from scratch) —</option>
          {chTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>

      <Field label={channel === "sms" ? "Send to (phone)" : "Send to (email)"}>
        <input style={S.input} value={to} onChange={(e) => setTo(e.target.value)} placeholder={channel === "sms" ? "+1…" : "name@example.com"} />
      </Field>

      {channel === "email" && (
        <Field label="Subject"><input style={S.input} value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      )}

      <Field label="Message" hint={`Placeholders: ${PLACEHOLDER_HELP}`}>
        <textarea style={{ ...S.input, minHeight: 120, resize: "vertical" }} value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>

      <Field label="Schedule for (optional)" hint="Leave blank to save as a draft or send now.">
        <input type="datetime-local" style={{ ...S.input, appearance: "auto" }} value={when} onChange={(e) => setWhen(e.target.value)} />
      </Field>

      {msg && <div style={{ marginBottom: 12 }}><Chip tone={msg.tone}>{msg.text}</Chip></div>}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
        <Btn onClick={() => doSave("test")} disabled={busy || !body}>📩 Send test to me</Btn>
        <Btn onClick={() => doSave("draft")} disabled={busy || !body}>Save draft</Btn>
        <Btn onClick={() => doSave("schedule")} disabled={busy || !body || !when}>Schedule</Btn>
        <Btn kind="pri" onClick={() => doSave("send")} disabled={busy || !body || !to}>
          {settings?.armed ? "Send now" : "Send now (needs arming)"}
        </Btn>
      </div>
    </Modal>
  );
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function EditQueueItemModal({ item, onClose, onDone }) {
  const [to, setTo] = useState(item.to_addr || "");
  const [subject, setSubject] = useState(item.subject || "");
  const [body, setBody] = useState(item.body || "");
  const [when, setWhen] = useState(toLocalInput(item.scheduled_for));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setBusy(true); setErr(null);
    const r = await callApi("schedule-update", {
      id: item.id, to_addr: to, subject, body,
      scheduled_for: when ? new Date(when).toISOString() : null,
    });
    setBusy(false);
    if (r.ok) onDone(); else setErr(r.error || "Save failed");
  };
  return (
    <Modal title="Edit message" onClose={onClose}>
      <Field label={item.channel === "sms" ? "To (phone)" : "To (email)"}><input style={S.input} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      {item.channel === "email" && <Field label="Subject"><input style={S.input} value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>}
      <Field label="Message"><textarea style={{ ...S.input, minHeight: 110, resize: "vertical" }} value={body} onChange={(e) => setBody(e.target.value)} /></Field>
      <Field label="Scheduled for" hint="Blank = send on the next pass once approved."><input type="datetime-local" style={{ ...S.input, appearance: "auto" }} value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
      {err && <div style={{ marginBottom: 10 }}><Chip tone="failed">{err}</Chip></div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn kind="pri" onClick={save} disabled={busy || !body}>Save</Btn>
      </div>
    </Modal>
  );
}

function QueuePanel({ items, settings, onRefresh, onCompose }) {
  const [busyId, setBusyId] = useState(null);
  const [rowMsg, setRowMsg] = useState({});
  const [editItem, setEditItem] = useState(null);
  const flash = (id, tone, text) => setRowMsg((m) => ({ ...m, [id]: { tone, text } }));

  const sendTest = async (it) => {
    setBusyId(it.id);
    const r = await callApi("send-now", { id: it.id, test: true });
    setBusyId(null);
    flash(it.id, r.ok ? "sent" : "failed", r.ok ? `Test sent to you (${r.to})` : `Test failed — ${r.error || "unknown"}`);
    onRefresh();
  };
  const sendNow = async (it) => {
    if (!window.confirm(`Send this ${it.channel === "sms" ? "text" : "email"} to ${it.to_addr} right now?`)) return;
    setBusyId(it.id);
    const r = await callApi("send-now", { id: it.id });
    setBusyId(null);
    flash(it.id, r.ok ? "sent" : "failed",
      r.ok ? `Sent to ${r.to}` : (r.error === "disarmed" ? "Safe mode is on — arm comms first, or use Test." : `Send failed — ${r.error}`));
    onRefresh();
  };
  const setStatus = async (it, status, note) => {
    setBusyId(it.id);
    const r = await callApi("schedule-update", { id: it.id, status });
    setBusyId(null);
    if (note && r.ok) flash(it.id, "scheduled", note);
    onRefresh();
  };
  const cancel = async (it) => { setBusyId(it.id); await callApi("schedule-cancel", { id: it.id }); setBusyId(null); onRefresh(); };

  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={S.sectionH}>Message queue</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn kind="ghost" onClick={onRefresh}>↻</Btn>
          <Btn kind="pri" onClick={() => onCompose(null)}>+ New message</Btn>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>
        <b>Approve</b> queues a draft to send at its scheduled time · <b>Send</b> fires immediately · <b>Test</b> goes to you only.
      </div>
      {!items.length ? <Empty>No messages yet. Create one with “New message”, or generate drafts from a rule.</Empty> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={S.th}></th><th style={S.th}>Client</th><th style={S.th}>To</th>
              <th style={S.th}>Preview</th><th style={S.th}>Status</th><th style={S.th}>When</th><th style={S.th}></th>
            </tr></thead>
            <tbody>
              {items.map((it) => {
                const m = rowMsg[it.id];
                const actionable = ["draft", "scheduled", "failed"].includes(it.status);
                return (
                <tr key={it.id}>
                  <td style={{ ...S.td, fontSize: 16 }}>{CHANNEL_ICON[it.channel]}</td>
                  <td style={{ ...S.td, fontWeight: 600, whiteSpace: "nowrap" }}>{it.client_name || "—"}{it.is_test ? <span style={{ color: C.faint, fontWeight: 400 }}> (test)</span> : ""}</td>
                  <td style={{ ...S.td, color: C.muted, whiteSpace: "nowrap" }}>{it.to_addr || <span style={{ color: C.red }}>no recipient</span>}</td>
                  <td style={{ ...S.td, color: C.muted, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.subject ? `${it.subject} — ` : ""}{it.body}
                    {m && <div style={{ marginTop: 4 }}><Chip tone={m.tone}>{m.text}</Chip></div>}
                  </td>
                  <td style={S.td}><Chip tone={it.status}>{titleCase(it.status)}</Chip></td>
                  <td style={{ ...S.td, whiteSpace: "nowrap", color: C.faint, fontSize: 12 }}>{it.sent_at ? fmtDateTime(it.sent_at) : it.scheduled_for ? fmtDateTime(it.scheduled_for) : "—"}</td>
                  <td style={{ ...S.td, whiteSpace: "nowrap" }}>
                    {actionable && (
                      <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {it.status === "draft" && <Btn kind="ghost" disabled={busyId === it.id} onClick={() => setStatus(it, "scheduled", "Queued")} style={{ color: C.blue, borderColor: "rgba(59,130,246,.4)" }}>Approve</Btn>}
                        {it.status === "scheduled" && <Btn kind="ghost" disabled={busyId === it.id} onClick={() => setStatus(it, "draft")}>Unqueue</Btn>}
                        <Btn kind="ghost" disabled={busyId === it.id} onClick={() => setEditItem(it)}>Edit</Btn>
                        <Btn kind="ghost" disabled={busyId === it.id} onClick={() => sendTest(it)}>Test</Btn>
                        <Btn kind="ghost" disabled={busyId === it.id} onClick={() => sendNow(it)} style={settings?.armed ? { color: C.lime, borderColor: "rgba(165,206,58,.4)" } : {}}>Send</Btn>
                        <Btn kind="ghost" disabled={busyId === it.id} onClick={() => cancel(it)}>✕</Btn>
                      </span>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editItem && <EditQueueItemModal item={editItem} onClose={() => setEditItem(null)} onDone={() => { setEditItem(null); onRefresh(); }} />}
    </div>
  );
}

function TemplatesPanel({ templates, onRefresh }) {
  const [edit, setEdit] = useState(null);
  const save = async () => { await callApi("template-save", edit); setEdit(null); onRefresh(); };
  const del = async (id) => { if (window.confirm("Delete this template?")) { await callApi("template-delete", { id }); onRefresh(); } };
  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={S.sectionH}>Templates</div>
        <Btn kind="pri" onClick={() => setEdit({ name: "", channel: "sms", subject: "", body: "", category: "" })}>+ New template</Btn>
      </div>
      {!templates.length ? <Empty>No templates.</Empty> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px,1fr))", gap: 10 }}>
          {templates.map((t) => (
            <div key={t.id} style={{ background: C.card2, borderRadius: 10, padding: 12, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 13.5 }}>{t.name}</span>
                <Chip tone={t.channel}>{t.channel.toUpperCase()}</Chip>
              </div>
              {t.subject && <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{t.subject}</div>}
              <div style={{ fontSize: 12, color: C.faint, maxHeight: 54, overflow: "hidden", lineHeight: 1.4 }}>{t.body}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                <Btn kind="ghost" onClick={() => setEdit(t)}>Edit</Btn>
                <Btn kind="ghost" onClick={() => del(t.id)}>Delete</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      {edit && (
        <Modal title={edit.id ? "Edit template" : "New template"} onClose={() => setEdit(null)}>
          <Field label="Name"><input style={S.input} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          <Field label="Channel">
            <select style={{ ...S.input, appearance: "auto" }} value={edit.channel} onChange={(e) => setEdit({ ...edit, channel: e.target.value })}>
              <option value="sms">Text (SMS)</option><option value="email">Email</option>
            </select>
          </Field>
          {edit.channel === "email" && <Field label="Subject"><input style={S.input} value={edit.subject || ""} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} /></Field>}
          <Field label="Body" hint={`Placeholders: ${PLACEHOLDER_HELP}`}>
            <textarea style={{ ...S.input, minHeight: 140, resize: "vertical" }} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} />
          </Field>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn onClick={() => setEdit(null)}>Cancel</Btn>
            <Btn kind="pri" onClick={save} disabled={!edit.name || !edit.body}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RulesPanel({ rules, triggers, templates, onRefresh, refreshQueue }) {
  const [edit, setEdit] = useState(null);
  const [gen, setGen] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const save = async () => { await callApi("rule-save", edit); setEdit(null); onRefresh(); };
  const del = async (id) => { if (window.confirm("Delete this rule?")) { await callApi("rule-delete", { id }); onRefresh(); } };
  const toggle = async (r) => { await callApi("rule-save", { ...r, active: !r.active }); onRefresh(); };
  const generate = async () => {
    setGenBusy(true); setGen(null);
    const r = await callApi("comms-generate");
    setGenBusy(false);
    if (r.ok) {
      const total = (r.summary || []).reduce((a, s) => a + (s.created || 0), 0);
      setGen({ ok: true, total, active: r.activeRules, summary: r.summary || [] });
      refreshQueue && refreshQueue();
    } else setGen({ ok: false, error: r.error });
  };
  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8, flexWrap: "wrap" }}>
        <div style={S.sectionH}>Automation rules</div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn disabled={genBusy} onClick={generate}>{genBusy ? "Generating…" : "⚙ Generate drafts now"}</Btn>
          <Btn kind="pri" onClick={() => setEdit({ name: "", trigger_type: triggers[0], channel: "sms", template_id: "", offset_hours: 0, active: false, auto_approve: false })}>+ New rule</Btn>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>
        Rules generate <b>draft</b> messages for your review unless “auto-approve” is on. Nothing sends until comms are armed. Drafts also generate automatically every 15 min while a rule is active.
      </div>
      {gen && (
        <div style={{ marginBottom: 12 }}>
          {gen.ok
            ? <Chip tone={gen.total ? "sent" : "muted"}>{gen.total ? `Created ${gen.total} draft${gen.total > 1 ? "s" : ""} from ${gen.active} active rule${gen.active > 1 ? "s" : ""} — see the Queue tab` : `No new drafts (${gen.active} active rule${gen.active === 1 ? "" : "s"})`}</Chip>
            : <Chip tone="failed">Generate failed: {gen.error}</Chip>}
        </div>
      )}
      {!rules.length ? <Empty>No rules yet.</Empty> : rules.map((r) => (
        <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", background: C.card2, borderRadius: 8, marginBottom: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{r.name} <Chip tone={r.channel}>{r.channel.toUpperCase()}</Chip></div>
            <div style={{ fontSize: 11.5, color: C.faint }}>{titleCase(r.trigger_type)} · {r.offset_hours >= 0 ? `+${r.offset_hours}h` : `${r.offset_hours}h`}{r.auto_approve ? " · auto-approve" : " · drafts"}</div>
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Chip tone={r.active ? "active" : "muted"}>{r.active ? "On" : "Off"}</Chip>
            <Btn kind="ghost" onClick={() => toggle(r)}>{r.active ? "Disable" : "Enable"}</Btn>
            <Btn kind="ghost" onClick={() => setEdit(r)}>Edit</Btn>
            <Btn kind="ghost" onClick={() => del(r.id)}>✕</Btn>
          </div>
        </div>
      ))}
      {edit && (
        <Modal title={edit.id ? "Edit rule" : "New rule"} onClose={() => setEdit(null)}>
          <Field label="Name"><input style={S.input} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></Field>
          <Field label="Trigger">
            <select style={{ ...S.input, appearance: "auto" }} value={edit.trigger_type} onChange={(e) => setEdit({ ...edit, trigger_type: e.target.value })}>
              {triggers.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </select>
          </Field>
          <Field label="Channel">
            <select style={{ ...S.input, appearance: "auto" }} value={edit.channel} onChange={(e) => setEdit({ ...edit, channel: e.target.value })}>
              <option value="sms">Text (SMS)</option><option value="email">Email</option>
            </select>
          </Field>
          <Field label="Template">
            <select style={{ ...S.input, appearance: "auto" }} value={edit.template_id || ""} onChange={(e) => setEdit({ ...edit, template_id: e.target.value })}>
              <option value="">— None —</option>
              {templates.filter((t) => t.channel === edit.channel).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Offset (hours)" hint="Hours before/after the trigger event (e.g. -24 = a day before an appointment, +48 = two days after a quote).">
            <input type="number" style={{ ...S.input, appearance: "auto" }} value={edit.offset_hours} onChange={(e) => setEdit({ ...edit, offset_hours: Number(e.target.value) })} />
          </Field>
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={!!edit.auto_approve} onChange={(e) => setEdit({ ...edit, auto_approve: e.target.checked })} />
            Auto-approve (queue directly instead of creating a draft)
          </label>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Btn onClick={() => setEdit(null)}>Cancel</Btn>
            <Btn kind="pri" onClick={save} disabled={!edit.name}>Save</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

function InboxPanel({ settings }) {
  const [threads, setThreads] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  useEffect(() => { activeRef.current = active; }, [active]);

  const loadThreads = useCallback(async () => {
    setErr(null);
    const r = await callApi("inbox-threads", { limit: 40 });
    if (r.ok) setThreads(r.threads); else { setThreads([]); setErr(r.error); }
  }, []);
  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const openThread = useCallback(async (t) => {
    // Reset the reply box on every thread switch — otherwise half-typed text meant
    // for one client would send to the next one you open (wrong-recipient bug).
    setActive(t); setMessages([]); setLoadingMsgs(true); setSendMsg(null); setReply("");
    const r = await callApi("inbox-thread", { phoneNumberId: t.phoneNumberId, phone: t.phone, limit: 60 });
    setMessages(r.ok ? r.messages : []);
    setLoadingMsgs(false);
  }, []);

  const send = async (test) => {
    if (!reply.trim() || !active) return;
    const target = active;          // capture the target now — active may change mid-send
    const text = reply.trim();
    setSending(true); setSendMsg(null);
    const r = await callApi("inbox-send", {
      to: target.phone, phone: target.phone, fromNumberId: target.phoneNumberId,
      text, clientId: target.clientId, clientName: target.name, test,
    });
    setSending(false);
    if (!r.ok) { setSendMsg({ tone: "failed", text: r.error === "disarmed" ? "Safe mode is on — arm comms to reply (or use Test)." : `Failed — ${r.error}` }); return; }
    setSendMsg({ tone: "sent", text: test ? "Test sent to you" : "Sent ✓" });
    setReply("");
    // Optimistically show the sent bubble (OpenPhone's list is eventually consistent,
    // so a blind refetch could miss it and read as "not sent" → double-send). Only if
    // the operator hasn't switched away to another thread.
    if (!test && activeRef.current && activeRef.current.id === target.id) {
      setMessages((prev) => [...prev, { id: "sent-" + Date.now(), direction: "outgoing", text, createdAt: new Date().toISOString(), status: "sent" }]);
    }
  };

  if (threads === null) return <Spinner label="Loading conversations…" />;
  if (err) return (
    <div style={{ ...S.card, color: C.muted }}>
      <div style={{ color: C.amber, fontWeight: 700, marginBottom: 6 }}>Inbox unavailable</div>
      {err}
    </div>
  );

  return (
    <div style={{ ...S.card, padding: 0, display: "flex", minHeight: 460, overflow: "hidden", flexWrap: "wrap" }}>
      {/* Thread list */}
      <div style={{ flex: "1 1 260px", maxWidth: 320, borderRight: `1px solid ${C.border}`, minHeight: 200 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
          <span style={S.sectionH}>Conversations</span>
          <Btn kind="ghost" onClick={loadThreads}>↻</Btn>
        </div>
        <div style={{ maxHeight: 520, overflowY: "auto" }}>
          {!threads.length ? <Empty>No recent conversations.</Empty> : threads.map((t) => (
            <div key={t.id} onClick={() => openThread(t)} style={{
              padding: "11px 14px", cursor: "pointer", borderBottom: `1px solid ${C.card2}`,
              background: active?.id === t.id ? C.limeDim : "transparent",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: active?.id === t.id ? C.lime : C.text }}>{t.name || t.phone}</span>
                <span style={{ fontSize: 11, color: C.faint, whiteSpace: "nowrap" }}>{t.lastActivityAt ? fmtDate(t.lastActivityAt) : ""}</span>
              </div>
              <div style={{ fontSize: 11.5, color: C.faint }}>{t.name ? t.phone : ""}{t.lastPreview ? ` · ${t.lastPreview}` : ""}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div style={{ flex: "2 1 340px", display: "flex", flexDirection: "column", minHeight: 420 }}>
        {!active ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.faint, fontSize: 13, padding: 20 }}>
            Pick a conversation to view the thread and reply from the Quo line.
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{active.name || active.phone}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{active.phone}</div>
              </div>
              <Chip tone="sms">Quo line</Chip>
            </div>
            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8, maxHeight: 360 }}>
              {loadingMsgs ? <Spinner /> : !messages.length ? <Empty>No messages in this thread.</Empty> : messages.map((m) => {
                const out = m.direction === "outgoing";
                return (
                  <div key={m.id} style={{ alignSelf: out ? "flex-end" : "flex-start", maxWidth: "78%" }}>
                    <div style={{
                      padding: "8px 12px", borderRadius: 14, fontSize: 13.5, lineHeight: 1.4,
                      background: out ? C.lime : C.card, color: out ? "#12200a" : C.text,
                      borderBottomRightRadius: out ? 4 : 14, borderBottomLeftRadius: out ? 14 : 4,
                    }}>{m.text}</div>
                    <div style={{ fontSize: 10, color: C.faint, textAlign: out ? "right" : "left", marginTop: 2 }}>
                      {m.createdAt ? fmtDateTime(m.createdAt) : ""}{out && m.status ? ` · ${m.status}` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ borderTop: `1px solid ${C.border}`, padding: 12 }}>
              {sendMsg && <div style={{ marginBottom: 8 }}><Chip tone={sendMsg.tone}>{sendMsg.text}</Chip></div>}
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type a reply…" style={{ ...S.input, minHeight: 54, resize: "vertical", marginBottom: 8 }} />
              <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, color: C.faint }}>
                  {settings?.armed ? "🟢 Replies send live from the Quo line" : "🟡 Safe mode — arm to reply to clients; Test goes to you"}
                </span>
                <span style={{ display: "flex", gap: 8 }}>
                  <Btn onClick={() => send(true)} disabled={sending || !reply.trim()}>Test to me</Btn>
                  <Btn kind="pri" onClick={() => send(false)} disabled={sending || !reply.trim()}>{settings?.armed ? "Send" : "Send (needs arming)"}</Btn>
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CommsTab({ composeClient, clearComposeClient }) {
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [queue, setQueue] = useState([]);
  const [rules, setRules] = useState({ rules: [], triggers: [] });
  const [sub, setSub] = useState("inbox");
  const [compose, setCompose] = useState(null);
  const [armBusy, setArmBusy] = useState(false);

  const loadSettings = useCallback(async () => { const r = await callApi("settings"); if (r.ok) setSettings(r.settings); }, []);
  const loadTemplates = useCallback(async () => { const r = await callApi("templates"); if (r.ok) setTemplates(r.templates); }, []);
  const loadQueue = useCallback(async () => { const r = await callApi("scheduled"); if (r.ok) setQueue(r.scheduled); }, []);
  const loadRules = useCallback(async () => { const r = await callApi("rules"); if (r.ok) setRules({ rules: r.rules, triggers: r.triggers }); }, []);

  useEffect(() => { loadSettings(); loadTemplates(); loadQueue(); loadRules(); }, [loadSettings, loadTemplates, loadQueue, loadRules]);

  // A "Message" click from the Clients tab opens compose here.
  useEffect(() => {
    if (composeClient) { setSub("queue"); setCompose({ client: composeClient }); clearComposeClient(); }
  }, [composeClient, clearComposeClient]);

  const toggleArm = async (next) => {
    if (next && !window.confirm("Go LIVE? Scheduled messages will start sending to real clients at their due time.")) return;
    setArmBusy(true);
    const r = await callApi("settings-set", { armed: next });
    if (r.ok) setSettings(r.settings);
    setArmBusy(false);
  };

  if (!settings) return <Spinner label="Loading communications…" />;

  const SUBS = [["inbox", "Inbox"], ["queue", "Queue"], ["templates", "Templates"], ["rules", "Automation"]];

  return (
    <div>
      <ArmBanner settings={settings} onToggle={toggleArm} busy={armBusy} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        {SUBS.map(([k, label]) => (
          <button key={k} onClick={() => setSub(k)} style={{
            padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 13,
            border: `1px solid ${sub === k ? C.lime : C.border}`, color: sub === k ? C.lime : C.muted,
            background: sub === k ? C.limeDim : "transparent",
          }}>{label}</button>
        ))}
      </div>

      {sub === "inbox" && <InboxPanel settings={settings} />}
      {sub === "queue" && <QueuePanel items={queue} settings={settings} onRefresh={loadQueue} onCompose={() => setCompose({})} />}
      {sub === "templates" && <TemplatesPanel templates={templates} onRefresh={loadTemplates} />}
      {sub === "rules" && <RulesPanel rules={rules.rules} triggers={rules.triggers} templates={templates} onRefresh={loadRules} refreshQueue={loadQueue} />}

      {compose && (
        <ComposeModal
          initialClient={compose.client || null}
          templates={templates}
          settings={settings}
          onClose={() => setCompose(null)}
          onDone={() => { setCompose(null); loadQueue(); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MONEY — QuickBooks AR (source of truth) + create invoices, Jobber-independent
// ═══════════════════════════════════════════════════════════════════════════
function CreateInvoiceModal({ job, onClose, onDone }) {
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState("");
  const [amount, setAmount] = useState(
    job.remaining ? String(Math.round(job.remaining))   // partial job — default to the unbilled balance
    : job.quoted ? String(Math.round(job.quoted))
    : (job.revenue ? String(Math.round(job.revenue)) : "")
  );
  const [desc, setDesc] = useState(job.title || "");
  const [tax, setTax] = useState("gst");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const taxLabel = { gst: "GST only (5%)", gst_pst: "GST + PST BC (12%)", exempt: "Exempt / no tax" };

  useEffect(() => { (async () => { const r = await callApi("qbo-items"); if (r.ok) setItems(r.items); })(); }, []);

  const doPreview = async () => {
    setBusy(true); setMsg(null); setPreview(null);
    const r = await callApi("invoice-preview", { jobNum: job.jobNum, clientName: job.clientName, amount: Number(amount), itemId, description: desc, taxTreatment: tax });
    setBusy(false);
    if (r.ok) setPreview(r); else setMsg({ tone: "failed", text: r.error });
  };
  const doCreate = async () => {
    if (!window.confirm(`Create a DRAFT invoice in QuickBooks for ${preview.customer.name} — $${Math.round(preview.amount)} (${taxLabel[tax]})?\n\nThis creates a real invoice in QuickBooks (not emailed — you review & send it there).`)) return;
    setBusy(true); setMsg(null);
    const r = await callApi("invoice-create", { jobNum: job.jobNum, clientName: job.clientName, amount: Number(amount), itemId, description: desc, taxTreatment: tax });
    setBusy(false);
    if (r.ok) { setMsg({ tone: "sent", text: `Created QuickBooks invoice #${r.docNumber} (draft — review & send in QBO).` }); setTimeout(onDone, 1400); }
    else setMsg({ tone: "failed", text: r.error });
  };

  return (
    <Modal title={`Invoice — ${job.clientName || "Job #" + job.jobNum}`} onClose={onClose}>
      <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>Creates a <b>draft</b> invoice in QuickBooks. It is <b>not</b> emailed — you review and send it from QuickBooks.</div>
      <Field label="Amount (pre-GST)"><input style={S.input} value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="e.g. 1250" /></Field>
      <Field label="Service item (QuickBooks)" hint="Sets the income account. Pick the closest match.">
        <select style={{ ...S.input, appearance: "auto" }} value={itemId} onChange={(e) => setItemId(e.target.value)}>
          <option value="">— pick an item —</option>
          {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </Field>
      <Field label="Description"><input style={S.input} value={desc} onChange={(e) => setDesc(e.target.value)} /></Field>
      <Field label="Tax" hint="GST+PST for material-inclusive invoices; GST-only for labour.">
        <select style={{ ...S.input, appearance: "auto" }} value={tax} onChange={(e) => setTax(e.target.value)}>
          <option value="gst">GST only (5%)</option>
          <option value="gst_pst">GST + PST BC (12%)</option>
          <option value="exempt">Exempt / no tax</option>
        </select>
      </Field>
      {preview && (
        <div style={{ ...S.card, background: C.card2, marginBottom: 12 }}>
          <div style={{ fontSize: 12.5 }}>Matched QuickBooks customer: <b style={{ color: C.lime }}>{preview.customer.name}</b></div>
          <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>${Math.round(preview.amount).toLocaleString()} + GST · {desc}</div>
        </div>
      )}
      {msg && <div style={{ marginBottom: 12 }}><Chip tone={msg.tone}>{msg.text}</Chip></div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn onClick={doPreview} disabled={busy || !amount || !itemId}>Preview</Btn>
        <Btn kind="pri" onClick={doCreate} disabled={busy || !preview}>Create draft in QuickBooks</Btn>
      </div>
    </Modal>
  );
}

function MoneyTab() {
  const [ar, setAr] = useState(null);
  const [rti, setRti] = useState(null);
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState(null);
  const [invoiceJob, setInvoiceJob] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(async () => {
    setErr(null);
    const [a, r, m] = await Promise.all([callApi("native-ar"), callApi("ready-to-invoice"), callApi("money-report")]);
    if (a.ok) setAr(a); else { setAr({ invoices: [] }); setErr(a.error || "Read failed"); }
    setRti(r.ok ? r.jobs : []);
    setRep(m.ok ? m : null);
  }, []);
  const sync = async () => { setSyncing(true); await callApi("invoices-sync"); setSyncing(false); load(); };
  useEffect(() => { load(); }, [load]);

  if (!ar) return <Spinner label="Loading invoices…" />;
  const b = ar.buckets || {};
  const agingRow = [["Current", b.current, C.muted], ["1–30d", b.d1_30, C.amber], ["31–60d", b.d31_60, C.amber], ["61–90d", b.d61_90, C.red], ["90+d", b.d90plus, C.red]];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12.5, color: C.muted }}>
          ElecBid invoices — <b style={{ color: C.text }}>native tracking</b>, linked to QuickBooks.{ar.syncedAt ? <span style={{ color: C.faint }}> · synced {fmtDateTime(ar.syncedAt)}</span> : null}
        </div>
        <Btn kind="ghost" disabled={syncing} onClick={sync}>{syncing ? "Syncing…" : "↻ Sync from QuickBooks"}</Btn>
      </div>
      {err && <div style={{ ...S.card, color: C.amber }}>{err}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginBottom: 14 }}>
        <div style={{ ...S.card, margin: 0 }}><div style={{ fontSize: 26, fontWeight: 800, color: C.red }}>{money(ar.totalOwing)}</div><div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Outstanding (QBO)</div></div>
        <div style={{ ...S.card, margin: 0 }}><div style={{ fontSize: 26, fontWeight: 800, color: C.text }}>{ar.count || 0}</div><div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Open invoices</div></div>
        <div style={{ ...S.card, margin: 0 }}><div style={{ fontSize: 26, fontWeight: 800, color: C.lime }}>{(rti || []).length}</div><div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>Ready to invoice</div></div>
      </div>

      <div style={{ ...S.card, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <div style={{ ...S.sectionH, width: "100%", marginBottom: 4 }}>Aging</div>
        {agingRow.map(([label, val, color]) => (
          <div key={label} style={{ flex: "1 1 90px", padding: "8px 10px", background: C.card2, borderRadius: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color }}>{money(val)}</div>
            <div style={{ fontSize: 10.5, color: C.muted }}>{label}</div>
          </div>
        ))}
      </div>

      {rep && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
            <div style={S.sectionH}>Reports</div>
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: C.muted }}>DSO <b style={{ color: C.text, fontSize: 15 }}>{rep.dso != null ? rep.dso + "d" : "—"}</b></span>
              <span style={{ fontSize: 12, color: C.muted }}>Avg days to pay <b style={{ color: C.text, fontSize: 15 }}>{rep.avgDaysToPay != null ? rep.avgDaysToPay + "d" : "—"}</b></span>
            </div>
          </div>
          {!(rep.revenueByMonth || []).length ? <Empty>No revenue history yet — sync from QuickBooks.</Empty> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={S.th}>Month</th><th style={{ ...S.th, textAlign: "right" }}>Pre-tax</th><th style={{ ...S.th, textAlign: "right" }}>GST</th><th style={{ ...S.th, textAlign: "right" }}>Tax-in</th><th style={{ ...S.th, textAlign: "right" }}>Invoices</th></tr></thead>
                <tbody>
                  {rep.revenueByMonth.slice(0, 8).map((m) => (
                    <tr key={m.month}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{m.month}</td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: C.lime }}>{money(m.preTax)}</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.muted }}>{money(m.gst)}</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.text }}>{money(m.taxIn)}</td>
                      <td style={{ ...S.td, textAlign: "right", color: C.faint }}>{m.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 14 }}>
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={S.sectionH}>Outstanding invoices</div><Btn kind="ghost" onClick={load}>↻</Btn>
          </div>
          {!(ar.invoices || []).length ? <Empty>Nothing outstanding.</Empty> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={S.th}>#</th><th style={S.th}>Customer</th><th style={S.th}>Balance</th><th style={S.th}>Due</th><th style={S.th}>Overdue</th></tr></thead>
                <tbody>
                  {ar.invoices.map((iv) => (
                    <tr key={iv.id}>
                      <td style={{ ...S.td, color: C.muted }}>{iv.number}</td>
                      <td style={{ ...S.td, fontWeight: 600, whiteSpace: "nowrap", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{iv.customer}</td>
                      <td style={{ ...S.td, fontWeight: 700, color: C.red, whiteSpace: "nowrap" }}>{money(iv.balance)}</td>
                      <td style={{ ...S.td, color: C.faint, fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(iv.dueDate)}</td>
                      <td style={S.td}>{iv.daysOverdue > 0 ? <Chip tone={iv.daysOverdue > 60 ? "failed" : "active"}>{iv.daysOverdue}d</Chip> : <span style={{ color: C.faint }}>—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={S.sectionH}>Ready to invoice (jobs done, not fully billed)</div>
          {!(rti || []).length ? <Empty>Nothing waiting to be invoiced.</Empty> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rti.map((j) => (
                <div key={j.jobNum} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "9px 11px", background: C.card2, borderRadius: 8, ...(j.partial ? { border: "1px solid rgba(245,158,11,.3)" } : {}) }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{j.clientName || `Job #${j.jobNum}`}</div>
                      {j.partial && <Chip tone="active">Follow-up</Chip>}
                    </div>
                    <div style={{ fontSize: 11.5, color: C.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      #{j.jobNum} {j.title || ""}
                      {j.partial
                        ? ` · billed ${money(j.invoiced)} of ${money(j.quoted)}${j.pctBilled != null ? ` (${j.pctBilled}%)` : ""} · ${money(j.remaining)} unbilled`
                        : (j.quoted ? ` · quoted ${money(j.quoted)}` : "")}
                    </div>
                  </div>
                  <Btn kind="ghost" onClick={() => setInvoiceJob(j)} style={{ color: C.lime, borderColor: "rgba(165,206,58,.4)", whiteSpace: "nowrap" }}>Invoice →</Btn>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {invoiceJob && <CreateInvoiceModal job={invoiceJob} onClose={() => setInvoiceJob(null)} onDone={() => { setInvoiceJob(null); load(); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Root
// ═══════════════════════════════════════════════════════════════════════════
export default function PipelineHub() {
  const perms = usePermissions() || {};
  const isAdmin = !!(perms.isAdmin || perms.isOwner);
  // Tab lives in the URL (?tab=) so the client-360 drill-downs deep-link and
  // the browser back button walks back out of them.
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (k) => { const n = new URLSearchParams(params); n.set("tab", k); setParams(n); };
  const [composeClient, setComposeClient] = useState(null);

  const goMessage = (client) => { setComposeClient(client); setTab("comms"); };

  if (!isAdmin) {
    return <div style={{ ...S.wrap }}><div style={{ ...S.card, color: C.muted }}>The Pipeline is available to admins and owners.</div></div>;
  }

  // The Schedule board moved to /schedule (2026-07-30, Dylan) — one schedule for
  // the whole company: day/week/month views, filters, dispatch writes. Old
  // ?tab=schedule deep links land there.
  if (tab === "schedule") return <Navigate to="/schedule" replace />;
  const TABS = [["overview", "Overview"], ["clients", "Clients"], ["money", "Money"], ["comms", "Communications"]];

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      <div style={S.wrap}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 4 }}>
          <h1 style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 34, letterSpacing: ".03em", margin: 0, color: C.text }}>
            ElecBid <span style={{ color: C.lime }}>Pipeline</span>
          </h1>
        </div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
          One workspace for the whole client → request → quote → job → invoice flow, plus scheduled client texts &amp; emails.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18, borderBottom: `1px solid ${C.border}`, paddingBottom: 12 }}>
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: "9px 18px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontWeight: 700, fontSize: 14,
              border: `1px solid ${tab === k ? C.lime : C.border}`, color: tab === k ? "#12200a" : C.muted,
              background: tab === k ? C.lime : "transparent",
            }}>{label}</button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab />}
        {tab === "clients" && <ClientsTab onMessage={goMessage} />}
        {tab === "money" && <MoneyTab />}
        {tab === "comms" && <CommsTab composeClient={composeClient} clearComposeClient={() => setComposeClient(null)} />}
      </div>
    </div>
  );
}
